import React, { useState, useEffect, useCallback, useMemo } from 'react'
import SearchBar from './search-bar'
import { makeStyles } from '@material-ui/styles'
import {
  LinearProgress,
  TableContainer,
  Box,
  Button,
  Typography,
} from '@material-ui/core'
import NewLeads from './new-leads'
import ContactedLeads from './contacted-leads'
import { useUpgradeFlow } from 'lib/hooks/use-upgrade-flow'
import UpgradeSuccessModal from 'components/upgrade-success-modal'
import OpenInNewOutlinedIcon from 'components/material-symbol/icons/OpenInNew'
import TablePagination from 'components/pagination/table-pagination'
import { partitionLeadsByContactStatus } from './utils'

export const useStyles = makeStyles(theme => ({
  leadNameCell: {
    paddingLeft: '1.5rem',
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
  containerStyles: {
    boxSizing: 'border-box',
    padding: theme.spacing(3, 4.5, 2),
    maxWidth: '100%',
    [theme.breakpoints.down('sm')]: {
      marginBottom: '60px',
      padding: theme.spacing(3, 1, 2),
    },
  },
  tableShell: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    backgroundColor: theme.palette.common.white,
    overflow: 'hidden',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    width: '100%',
    boxSizing: 'border-box',
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
    },
  },
  searchGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flex: '1 1 0',
    minWidth: 0,
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
      width: '100%',
    },
  },
  searchField: {
    flex: '1 1 0',
    minWidth: 0,
    maxWidth: 360,
    width: '100%',
  },
  toolbarActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flexShrink: 0,
    marginLeft: 'auto',
    [theme.breakpoints.down('sm')]: {
      marginLeft: 'auto',
    },
  },
  tableContent: {
    padding: theme.spacing(0, 1, 1),
  },
  coachPlusSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2.5),
    padding: theme.spacing(3),
    backgroundColor: '#E0EAFF',
    border: '1px solid #C5D4FF',
    borderRadius: 12,
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  coachPlusCopy: {
    flex: 1,
    minWidth: 0,
  },
  coachPlusTitle: {
    fontWeight: 700,
    fontSize: '1.125rem',
    lineHeight: 1.3,
    color: '#00205B',
    marginBottom: theme.spacing(0.75),
  },
  coachPlusCaption: {
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: '#172024',
  },
  coachPlusButton: {
    borderRadius: 20,
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '0.9375rem',
    padding: theme.spacing(1.25, 3.5),
    whiteSpace: 'nowrap',
    flexShrink: 0,
    boxShadow: 'none',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0, 83, 255, 0.28)',
    },
  },
  coachPlusBanner: {
    marginBottom: theme.spacing(2),
  },
}))

const LeadsTable = ({
  searchableItems = [],
  coachingLeadContactMethodOptions = [],
  refetch,
  freeTierLimitsReached,
  tab,
}) => {
  const classes = useStyles()
  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(0)
  const rowsPerPage = 25
  const isLeadsLimitReached = (freeTierLimitsReached ?? []).includes('LEADS')

  const {
    upgradeLoading: loading,
    handleUpgradeClick,
    showSuccessModal,
    handleDismiss,
  } = useUpgradeFlow('leads')

  const nameMatchesSearchToken = useCallback(
    ({ firstName = '', lastName = '' }, token) => {
      const fullName = `${firstName} ${lastName}`.toLowerCase().trim()
      const term = token.toLowerCase().trim()

      return fullName.includes(term)
    },
    [],
  )

  const filteredLeads = useMemo(
    () =>
      searchableItems.filter(item => nameMatchesSearchToken(item, searchTerm)),
    [nameMatchesSearchToken, searchableItems, searchTerm],
  )

  useEffect(() => {
    setPage(0)
  }, [searchTerm, tab])

  const sortByCreatedOn = useCallback((a, b, dir) => {
    a = a.props.lead.createdAt
    b = b.props.lead.createdAt

    if (a < b) return dir === 'desc' ? 1 : -1
    if (a === b) return 0
    if (a > b) return dir === 'desc' ? -1 : 1
    return 0
  }, [])

  const getLeadSortName = useCallback(lead => {
    const firstName = lead?.firstName ?? lead?.contact?.firstName ?? ''
    const lastName = lead?.lastName ?? lead?.contact?.lastName ?? ''
    return `${firstName}${lastName}`.toLowerCase().replace(/\s/g, '')
  }, [])

  const sortByFullName = useCallback(
    (a, b, dir) => {
      a = getLeadSortName(a.props.lead)
      b = getLeadSortName(b.props.lead)

      if (a > b) return dir === 'desc' ? 1 : -1
      if (a < b) return dir === 'desc' ? -1 : 1
      return 0
    },
    [getLeadSortName],
  )

  const { awaitingResponseLeads, contactedLeads } = useMemo(
    () => partitionLeadsByContactStatus(filteredLeads),
    [filteredLeads],
  )
  const currentLeads = tab === 'new' ? awaitingResponseLeads : contactedLeads
  const pageCount = Math.ceil((currentLeads?.length || 0) / rowsPerPage) || 1

  return (
    <>
      <TableContainer className={classes.containerStyles}>
        {loading && <LinearProgress color="primary" />}
        {isLeadsLimitReached && (
          <Box className={classes.coachPlusBanner}>
            <Box className={classes.coachPlusSection}>
              <Box className={classes.coachPlusCopy}>
                <Typography className={classes.coachPlusTitle}>
                  Unlock Unlimited Leads
                </Typography>
                <Typography className={classes.coachPlusCaption}>
                  Enjoy the benefits of your free account with up to two leads.
                  Want more? Upgrade to PGA Coach+ to receive unlimited coaching
                  requests, get featured placement on PGA.com, and access
                  premium profile tools and business insights to grow your
                  coaching business.
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="primary"
                className={classes.coachPlusButton}
                onClick={handleUpgradeClick}
                startIcon={<OpenInNewOutlinedIcon />}
              >
                Upgrade Now
              </Button>
            </Box>
          </Box>
        )}
        <Box className={classes.tableShell}>
          <Box className={classes.toolbar}>
            <Box className={classes.searchGroup}>
              <SearchBar
                className={classes.searchField}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                placeholder="Search Leads"
                data-testid="leads-list-search"
                style={{ margin: 0 }}
              />
            </Box>
            <Box className={classes.toolbarActions}>
              {currentLeads.length > 0 && (
                <TablePagination
                  page={page}
                  pageCount={pageCount}
                  rowsPerPage={rowsPerPage}
                  totalCount={currentLeads.length}
                  onPageChange={setPage}
                  alwaysShowCount
                />
              )}
            </Box>
          </Box>
          <Box className={classes.tableContent}>
            {tab === 'new' ? (
              <NewLeads
                awaitingResponseLeads={awaitingResponseLeads}
                contactMethodOptions={coachingLeadContactMethodOptions}
                refetch={refetch}
                sortByFullName={sortByFullName}
                sortByCreatedOn={sortByCreatedOn}
                page={page}
                onPageChange={setPage}
                rowsPerPage={rowsPerPage}
              />
            ) : (
              <ContactedLeads
                contactedLeads={contactedLeads}
                refetch={refetch}
                sortByFullName={sortByFullName}
                sortByCreatedOn={sortByCreatedOn}
                page={page}
                onPageChange={setPage}
                rowsPerPage={rowsPerPage}
              />
            )}
          </Box>
        </Box>
      </TableContainer>

      <UpgradeSuccessModal isOpen={showSuccessModal} onClose={handleDismiss} />
    </>
  )
}

export default LeadsTable
